import { Button, EmptyState, ListRow, ListRows, Skeleton, StatusTag, plural } from '@westhetics/ui'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { routes } from '@/routes'
import type { TodayResult } from './api'
import { MOBILE_NOTE } from './labels'
import { PendingAction } from './pending-block'

/**
 * "Hoje" (docs/areas/dashboard.md, "No celular"; RN-DSH-19 a 22): um cartão por atendimento com
 * horário, procedimentos, prestador e uma situação com ação. Abaixo de 900px é a composição inteira
 * do dashboard; no computador, é o que recepção e prestadora veem abaixo das pendências.
 */

export function TodayAgenda({
  data,
  isPending,
  error,
  onRetry,
  showNote,
}: {
  data: TodayResult | undefined
  isPending: boolean
  error: unknown
  onRetry: () => void
  /** A nota do celular: "Faturamento não aparece aqui". */
  showNote?: boolean
}) {
  if (error) {
    return (
      <LoadError
        title="Não conseguimos carregar a agenda de hoje"
        error={error}
        onRetry={onRetry}
      />
    )
  }
  if (!data) {
    return isPending ? (
      <div role="status" aria-label="Carregando a agenda" className="flex flex-col gap-3">
        <Skeleton className="h-[13px] w-[180px]" />
        <Skeleton className="h-[26px] w-[220px]" />
        <Skeleton className="h-[72px] w-full" />
        <Skeleton className="h-[72px] w-full" />
      </div>
    ) : null
  }
  return (
    <section aria-label="Agenda do dia" className="flex flex-col gap-4">
      <div>
        <div className="eyebrow">{data.label}</div>
        <h2 className="font-display text-ink mt-2 text-[26px] leading-[1.2] font-light">
          {data.title}
        </h2>
      </div>
      {data.appointments.length === 0 ? (
        <EmptyState
          title="Nenhum atendimento hoje"
          description="Os atendimentos entram aqui quando a data do procedimento é informada, ao gerar os documentos ou no plano."
          action={
            <Button variant="outline" asChild>
              <Link to={routes.patients}>Ver pacientes</Link>
            </Button>
          }
        />
      ) : (
        <ListRows>
          {data.appointments.map((a) => (
            <ListRow
              key={a.id}
              size="name"
              eyebrow={a.time ?? 'sem horário'}
              title={a.patient.name}
              meta={[a.procedures.join(', ') || null, a.practitioner?.name ?? null]
                .filter(Boolean)
                .join(' · ')}
              critical={false}
              trailing={
                <div className="flex flex-col items-end gap-2">
                  <StatusTag level={a.statusSummary.level}>{a.statusSummary.text}</StatusTag>
                  {a.statusSummary.action ? (
                    <PendingAction action={a.statusSummary.action} />
                  ) : null}
                  {a.statusSummary.morePendingItems > 0 ? (
                    <span className="font-body text-secondary text-[11px]">
                      +{plural(a.statusSummary.morePendingItems, 'pendência')}
                    </span>
                  ) : null}
                </div>
              }
            />
          ))}
        </ListRows>
      )}
      {showNote ? (
        <p className="font-body text-label text-[11px] leading-[1.6] font-light">{MOBILE_NOTE}</p>
      ) : null}
    </section>
  )
}
